import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, ArrowLeft, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PhoneInput from "@/components/PhoneInput";

const steps = ["Phone", "Verify", "Profile", "PIN"];

export default function CustomerRegister() {
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("+255");
  const [phoneStatus, setPhoneStatus] = useState("idle"); // idle | checking | available | taken
  const [otp, setOtp] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [ageConfirm, setAgeConfirm] = useState(false);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");

  const err = (e) => toast.error(apiError(e.response?.data?.detail) || e.message);

  const phoneDigits = (phone || "").replace(/\D/g, "").length;

  useEffect(() => {
    if (phoneDigits < 9) { setPhoneStatus("idle"); return; }
    let stale = false;
    setPhoneStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/auth/phone/available", { params: { phone } });
        if (!stale) setPhoneStatus(data.available ? "available" : "taken");
      } catch { if (!stale) setPhoneStatus("idle"); }
    }, 500);
    return () => { stale = true; clearTimeout(t); };
  }, [phone, phoneDigits]);

  const requestOtp = async () => {
    if (!phone) return toast.error("Enter your phone number");
    if (phoneStatus === "taken") return toast.error("This phone number is already registered. Please sign in instead.");
    if (phoneStatus === "checking") return toast.error("Checking your number, one moment…");
    setLoading(true);
    try {
      await api.post("/auth/otp/request", { phone });
      toast.success("Code sent to your phone");
      setStep(1);
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      await api.post("/auth/otp/verify", { phone, otp });
      setStep(2);
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const finish = async () => {
    if (pin !== pin2) return toast.error("PINs do not match");
    if (!(pin.length === 4 || pin.length === 6)) return toast.error("PIN must be 4 or 6 digits");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer/register", {
        phone, otp, full_name: fullName, email: email || null, pin,
        dob, age_confirmed: ageConfirm,
      });
      applyAuth(data);
      toast.success("OneKite account created!");
      nav("/app");
    } catch (e) { err(e); } finally { setLoading(false); }
  };

  const ageFrom = (d) => {
    if (!d) return null;
    const b = new Date(d);
    if (isNaN(b.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
    return a;
  };

  const proceedProfile = () => {
    if (!fullName) return toast.error("Enter your name");
    const age = ageFrom(dob);
    if (age === null) return toast.error("Enter your date of birth");
    if (age < 18) return toast.error("OneKite is currently available to customers aged 18 and above.");
    if (!ageConfirm) return toast.error("Please confirm that you are 18 years or older.");
    setStep(3);
  };

  return (
    <div className="min-h-screen ok-bg grain relative flex items-center justify-center px-5 py-10">
      <Link to="/" data-testid="back-home" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={18} /> Home
      </Link>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <Logo className="h-14" />
        </Link>

        <div className="card-surface p-7 fade-up">
          <div className="flex items-center gap-2 mb-6">
            {steps.map((s, i) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4">
              <h1 className="font-display font-bold text-2xl">What's your number?</h1>
              <p className="text-sm text-muted-foreground">We'll send a one-time code to verify it.</p>
              <div>
                <Label className="text-xs text-muted-foreground">Phone number</Label>
                <PhoneInput testid="reg-phone" value={phone} onChange={setPhone} />
                {phoneStatus === "checking" && (
                  <p data-testid="phone-status-checking" className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <CircleNotch size={13} className="animate-spin" /> Checking availability…
                  </p>
                )}
                {phoneStatus === "available" && (
                  <p data-testid="phone-status-available" className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-600">
                    <CheckCircle size={13} weight="fill" /> This number is available.
                  </p>
                )}
                {phoneStatus === "taken" && (
                  <p data-testid="phone-status-taken" className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                    <WarningCircle size={13} weight="fill" /> This number is already registered.{" "}
                    <Link to="/login" className="underline font-medium">Sign in</Link>
                  </p>
                )}
              </div>
              <Button data-testid="reg-request-otp" onClick={requestOtp} disabled={loading || phoneStatus === "taken" || phoneStatus === "checking"} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Sending…" : "Send code"}
              </Button>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <button onClick={() => setStep(0)} className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
              <h1 className="font-display font-bold text-2xl">Enter the code</h1>
              <p className="text-sm text-muted-foreground">We sent a 6-digit code by SMS to {phone}.</p>
              <Input data-testid="reg-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              <Button data-testid="reg-verify-otp" onClick={verifyOtp} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Verifying…" : "Verify"}
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h1 className="font-display font-bold text-2xl">Tell us about you</h1>
              <div>
                <Label className="text-xs text-muted-foreground">Full name</Label>
                <Input data-testid="reg-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Starla Matiku" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Email (optional)</Label>
                <Input data-testid="reg-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Date of birth</Label>
                <Input data-testid="reg-dob" type="date" max={new Date().toISOString().slice(0, 10)} value={dob} onChange={(e) => setDob(e.target.value)} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
                <p className="text-[11px] text-muted-foreground mt-1">OneKite is available to customers aged 18 and above. Your date of birth is kept private.</p>
              </div>
              <label data-testid="reg-age-confirm" className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={ageConfirm} onChange={(e) => setAgeConfirm(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-600 shrink-0" />
                <span className="text-xs text-muted-foreground leading-snug">I confirm that I am 18 years or older and that the information provided is accurate.</span>
              </label>
              <Button data-testid="reg-profile-next" onClick={proceedProfile} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                Continue
              </Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h1 className="font-display font-bold text-2xl">Create a secure PIN</h1>
              <p className="text-sm text-muted-foreground">4 or 6 digits. You'll use this to sign in.</p>
              <Input data-testid="reg-pin" type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit PIN" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              <Input data-testid="reg-pin2" type="password" inputMode="numeric" maxLength={4} value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Confirm 4-digit PIN" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono tracking-widest" />
              <Button data-testid="reg-finish" onClick={finish} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Creating…" : <><CheckCircle size={18} weight="fill" className="mr-2" /> Create my OneKite</>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
